import { useEffect, useRef, useState } from "react";
import { authStore } from "../../lib/api";
import { BACKEND } from "../../lib/chat";

/*
 * SSE 实时流（GET /api/chat/stream）
 *
 * 【原实现的问题（本次修复的起因）】
 * 旧版只依赖 EventSource 的 onerror 重连。但在 Windows + Docker Desktop 环境下，
 * 后端容器一旦重启，WSL 的 localhost 转发进程(wslrelay.exe)不会向浏览器补发 TCP
 * 断开(RST)，浏览器侧这条连接会残留成「僵尸连接」——状态仍是 ESTABLISHED，其实早已
 * 不通。此时 onerror 永远不会触发，于是既不重连也不释放；而 Chrome 对同一个 origin
 * 只允许 6 条并发连接，被僵尸 SSE 逐个占满后，页面上所有 API 请求全部排队 pending，
 * 表现为「前端页面在、点后端一直转圈」。
 *
 * 【本次修复：心跳看门狗】
 * 后端 /api/chat/stream 每 25s 必有数据：有事件就推事件，空闲 25s 就推一次
 * `event: ping` 心跳（见 backend/chat_hub.py）。因此前端只要记录「最后一次收到任何
 * 数据的时间」，一旦超过 STALE_MS 仍收不到任何东西，就可判定连接已僵死，主动
 * close() 并重连，从而把被占死的连接槽释放掉。
 *
 * 【保留的原有语义】
 * 连续 3 次 onerror → live=false，由调用方降级为 5s 轮询；重连仍由浏览器带
 * Last-Event-ID 续传。
 */

const STALE_MS = 45000; // 超过 45s 没有任何数据（含心跳）即判定连接僵死。后端心跳周期 25s，可容忍丢 1 次
const CHECK_MS = 10000; // 看门狗巡检间隔

export function useChatStream({ enabled, onEvent }) {
  const [live, setLive] = useState(false);
  const cb = useRef(onEvent);
  cb.current = onEvent;
  useEffect(() => {
    if (!enabled || typeof window.EventSource === "undefined") { setLive(false); return undefined; }
    let es = null;
    let fails = 0;
    let closed = false;
    let lastAt = Date.now(); // 最后一次收到任意 SSE 数据的时间（业务事件或 ping 心跳）
    let reopenTimer = null;

    // 刷新「存活时间」：任何数据到达（含 ping 心跳）都算连接活着
    const mark = () => { lastAt = Date.now(); };

    // 统一的重连入口：先关掉当前连接，再延迟重建；重复调用只会保留最后一个定时器
    const restart = (delay) => {
      if (closed) return;
      try { es?.close(); } catch { /* */ }
      setLive(false);
      clearTimeout(reopenTimer);
      reopenTimer = setTimeout(() => { if (!closed) open(); }, delay);
    };

    const open = () => {
      if (closed) return;
      lastAt = Date.now(); // 每次新建连接都重置计时，避免刚连上就被看门狗误判
      es = new window.EventSource(`${BACKEND}/api/chat/stream?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`);
      es.onopen = () => { fails = 0; setLive(true); mark(); };
      es.onerror = () => {
        fails += 1;
        if (fails >= 3) restart(30000); // 连续 3 次失败 → 交由调用方降级轮询，30s 后再尝试恢复
      };
      // 心跳：仅用于刷新存活时间，不转发给业务回调（事件名与业务事件不重叠）
      es.addEventListener("ping", mark);
      ["message", "update", "promote", "typing", "read", "draft", "channel", "remind"].forEach((ev) => es.addEventListener(ev, (e) => {
        mark();
        try { cb.current?.({ event: ev, data: JSON.parse(e.data) }); } catch { /* */ }
      }));
    };

    // 心跳看门狗：僵尸连接不会触发 onerror，只能靠「长时间收不到任何数据」来识别
    const watchdog = setInterval(() => {
      if (closed) return;
      if (Date.now() - lastAt > STALE_MS) restart(1000);
    }, CHECK_MS);

    open();
    return () => {
      closed = true;
      clearInterval(watchdog);
      clearTimeout(reopenTimer);
      try { es?.close(); } catch { /* */ }
    };
  }, [enabled]);
  return { live };
}
